import { sha256 } from '@noble/hashes/sha256';
import type { AuthUser, OidcDiscovery } from './types';

/**
 * Minimal JWT claims needed for OIDC ID tokens.
 * Using a plain object instead of jose's JWTPayload avoids any dependency on
 * the Web Crypto API — jose uses crypto.subtle.importKey internally which is
 * not available in React Native / Hermes.
 */
export interface JwtClaims {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  nonce?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Base64url helpers
// ---------------------------------------------------------------------------

function base64UrlToBytes(str: string): Uint8Array {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (b64.length % 4)) % 4;
  const binary = atob(b64 + '='.repeat(padding));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function decodeJwtPart(part: string): unknown {
  return JSON.parse(atob(
    part.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      part.length + (4 - (part.length % 4)) % 4, '=',
    ),
  )) as unknown;
}

// ---------------------------------------------------------------------------
// RS256 (RSA-PKCS#1-v1.5 + SHA-256) — pure BigInt, no Web Crypto required
// ---------------------------------------------------------------------------

// ASN.1 DER prefix for SHA-256 in PKCS#1 v1.5 DigestInfo
const SHA256_DIGEST_INFO = new Uint8Array([
  0x30, 0x31, 0x30, 0x0d, 0x06, 0x09, 0x60, 0x86,
  0x48, 0x01, 0x65, 0x03, 0x04, 0x02, 0x01, 0x05,
  0x00, 0x04, 0x20,
]);

function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = 1n;
  base %= mod;
  while (exp > 0n) {
    if (exp & 1n) result = result * base % mod;
    exp >>= 1n;
    base = base * base % mod;
  }
  return result;
}

function bytesToBigInt(b: Uint8Array): bigint {
  let r = 0n;
  for (const byte of b) r = (r << 8n) | BigInt(byte);
  return r;
}

function bigIntToFixedBytes(n: bigint, len: number): Uint8Array {
  const hex = n.toString(16).padStart(len * 2, '0');
  const pairs = hex.match(/.{2}/g);
  if (!pairs || pairs.length !== len) throw new Error('bigIntToFixedBytes: unexpected length');
  return Uint8Array.from(pairs.map(h => parseInt(h, 16)));
}

function verifyRS256(
  signedPart: Uint8Array,
  signature: Uint8Array,
  nBytes: Uint8Array,
  eBytes: Uint8Array,
): boolean {
  const n = bytesToBigInt(nBytes);
  const e = bytesToBigInt(eBytes);
  const sig = bytesToBigInt(signature);
  const keyLen = nBytes.length;

  const m = modPow(sig, e, n);
  const mBytes = bigIntToFixedBytes(m, keyLen);

  // PKCS#1 v1.5: 0x00 0x01 [0xff...] 0x00 [DigestInfo] [hash]
  if (mBytes[0] !== 0x00 || mBytes[1] !== 0x01) return false;
  let i = 2;
  while (i < mBytes.length && mBytes[i] === 0xff) i++;
  if (mBytes[i] !== 0x00) return false;
  i++;

  for (let j = 0; j < SHA256_DIGEST_INFO.length; j++) {
    if (mBytes[i + j] !== SHA256_DIGEST_INFO[j]) return false;
  }
  i += SHA256_DIGEST_INFO.length;

  const expected = sha256(signedPart);
  if (mBytes.length - i !== 32) return false;
  for (let j = 0; j < 32; j++) {
    if (mBytes[i + j] !== expected[j]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// JWKS fetching with a short in-memory cache
// ---------------------------------------------------------------------------

interface Jwk {
  kty: string;
  use?: string;
  kid?: string;
  alg?: string;
  n?: string; // RSA modulus (base64url)
  e?: string; // RSA exponent (base64url)
}

const jwksCache = new Map<string, { keys: Jwk[]; at: number }>();
const JWKS_TTL_MS = 5 * 60 * 1000;

async function fetchJwks(uri: string): Promise<Jwk[]> {
  const hit = jwksCache.get(uri);
  if (hit && Date.now() - hit.at < JWKS_TTL_MS) return hit.keys;
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`JWKS fetch failed: ${res.status}`);
  const { keys } = await res.json() as { keys: Jwk[] };
  jwksCache.set(uri, { keys, at: Date.now() });
  return keys;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface VerifyIdTokenInput {
  idToken: string;
  discovery: OidcDiscovery;
  /** Expected `aud` claim — usually the OIDC clientId. */
  audience: string;
  /** If provided, the `nonce` claim must match (as set during startLogin). */
  expectedNonce?: string;
}

/**
 * Verifies an RS256 ID token's signature, issuer, audience, expiry, and nonce.
 * Uses BigInt modular exponentiation + @noble/hashes/sha256 — no Web Crypto API.
 * Throws on any mismatch. Returns the decoded payload on success.
 */
export async function verifyIdToken(input: VerifyIdTokenInput): Promise<JwtClaims> {
  const parts = input.idToken.split('.');
  if (parts.length !== 3) throw new Error('Invalid ID token format');
  const [headerB64, payloadB64, sigB64] = parts as [string, string, string];

  const header = decodeJwtPart(headerB64) as { alg?: string; kid?: string };
  const payload = decodeJwtPart(payloadB64) as JwtClaims;

  if (header.alg !== 'RS256') {
    throw new Error(`Unsupported JWT algorithm: ${header.alg ?? 'none'} (only RS256 supported)`);
  }

  const keys = await fetchJwks(input.discovery.jwks_uri);
  const candidates = header.kid
    ? keys.filter(k => k.kty === 'RSA' && k.kid === header.kid)
    : keys.filter(k => k.kty === 'RSA');

  if (candidates.length === 0) {
    throw new Error(`No RSA key found in JWKS (kid=${header.kid ?? 'any'})`);
  }

  const signedPart = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
  const signature = base64UrlToBytes(sigB64);

  const verified = candidates.some(key => {
    if (!key.n || !key.e) return false;
    try {
      return verifyRS256(signedPart, signature, base64UrlToBytes(key.n), base64UrlToBytes(key.e));
    } catch {
      return false;
    }
  });

  if (!verified) throw new Error('ID token signature verification failed');

  // Validate standard claims
  const now = Math.floor(Date.now() / 1000);
  if (typeof payload.exp === 'number' && payload.exp < now) {
    throw new Error('ID token expired');
  }
  if (payload.iss !== input.discovery.issuer) {
    throw new Error(`ID token issuer mismatch: expected "${input.discovery.issuer}", got "${payload.iss}"`);
  }
  const audList = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
  if (!audList.includes(input.audience)) {
    throw new Error(`ID token audience mismatch: "${input.audience}" not in ${JSON.stringify(audList)}`);
  }
  if (input.expectedNonce !== undefined && payload.nonce !== input.expectedNonce) {
    throw new Error('ID token nonce mismatch');
  }

  return payload;
}

/** Project an ID token payload into the AuthUser shape used by the rest of the app. */
export function toAuthUser(claims: JwtClaims): AuthUser {
  if (typeof claims.sub !== 'string') {
    throw new Error('ID token missing required `sub` claim');
  }
  const c = claims as Record<string, unknown> & {
    preferred_username?: string;
    email?: string;
    name?: string;
    realm_access?: { roles?: string[] };
  };
  return {
    sub: claims.sub,
    preferredUsername: typeof c.preferred_username === 'string' ? c.preferred_username : undefined,
    email: typeof c.email === 'string' ? c.email : undefined,
    name: typeof c.name === 'string' ? c.name : undefined,
    realmRoles: c.realm_access?.roles ?? [],
    claims,
  };
}
